import { Link, NavLink, useNavigate } from "react-router-dom";
import { BookOpen, Film, GraduationCap, LayoutDashboard, LogOut, MessagesSquare, TrendingUp, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import BilingualText from "@/components/BilingualText";
import { t } from "@/lib/i18n";
import { cn as cx } from "@/lib/utils";
import { IDS } from "@/constants/testIds";

function NavItem({ to, cn, en, icon: Icon, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        cx(
          "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors",
          isActive
            ? "bg-slate-900 text-white"
            : "text-slate-600 hover:bg-slate-100",
        )
      }
    >
      <Icon className="w-4 h-4" />
      <span className="flex flex-col leading-none">
        <span className="cn text-[13px] font-bold">{cn}</span>
        <span className="text-[11px] opacity-70">{en}</span>
      </span>
    </NavLink>
  );
}

export function AppLayout({ children }) {
  const { user, guest, isAuthed, logout, currentProfile } = useAuth();
  const nav = useNavigate();

  const doLogout = () => {
    logout();
    nav("/");
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-3 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-sky-400 to-emerald-500 grid place-items-center shadow-sm">
              <GraduationCap className="w-5 h-5 text-white" />
            </span>
            <BilingualText cn={t.brand.cn} en={t.brand.en} size="sm" />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/dashboard" icon={LayoutDashboard} cn={t.nav_dashboard.cn} en={t.nav_dashboard.en} testid="nav-dashboard" />
            <NavItem to="/practice/reading" icon={BookOpen} cn={t.nav_practice.cn} en={t.nav_practice.en} testid="nav-practice" />
            <NavItem to="/lessons" icon={Film} cn="课程" en="Lessons" testid="nav-lessons" />
            <NavItem to="/progress" icon={TrendingUp} cn={t.nav_progress.cn} en={t.nav_progress.en} testid="nav-progress" />
            {user?.role === "student" && (
              <NavItem to="/chat" icon={MessagesSquare} cn="小甲" en="Prof. Scaly" testid="nav-chat" />
            )}
            {user?.role === "parent" && (
              <NavItem to="/parent" icon={Users} cn={t.nav_parent.cn} en={t.nav_parent.en} testid="nav-parent" />
            )}
          </nav>

          <div className="flex items-center gap-2">
            {currentProfile && (
              <span className="hidden sm:inline-flex badge-soft">
                {currentProfile.level || "—"} · {currentProfile.name || "Guest"}
              </span>
            )}
            {isAuthed ? (
              <Button
                variant="ghost"
                size="sm"
                data-testid={IDS.dashboard.logout}
                onClick={doLogout}
                className="rounded-full"
              >
                <LogOut className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Sign out</span>
              </Button>
            ) : guest ? (
              <Button
                asChild
                size="sm"
                className="rounded-full bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Link to="/auth">Sign up</Link>
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-8 py-8 sm:py-12">{children}</main>

      <footer className="mx-auto max-w-7xl px-4 sm:px-8 py-8 text-xs text-slate-400">
        <BilingualText
          cn="每日一点点，成绩大提升。"
          en="A little every day. Big progress over time."
          size="sm"
        />
      </footer>
    </div>
  );
}

export default AppLayout;
